import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Activity, ShieldCheck, Coins, Loader2, RefreshCcw,
  KeyRound, UserCog, Wand2, Sparkles, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PanelHeader } from './_shared';

const KIND_META = {
  role_change:          { Icon: UserCog,     cls: 'text-primary',      label: 'Role change' },
  role_preset_applied:  { Icon: ShieldCheck, cls: 'text-success',      label: 'Role preset applied' },
  password_reset:       { Icon: KeyRound,    cls: 'text-warning',      label: 'Password reset' },
  chapter:              { Icon: BookOpen,    cls: 'text-primary',      label: 'Chapter generation' },
  chat:                 { Icon: Wand2,       cls: 'text-primary',      label: 'AI chat' },
  outline:              { Icon: Sparkles,    cls: 'text-primary',      label: 'Outline' },
  revise:               { Icon: Sparkles,    cls: 'text-primary',      label: 'Revise' },
  build:                { Icon: BookOpen,    cls: 'text-primary',      label: 'Build novel' },
  usage:                { Icon: Coins,       cls: 'text-success',      label: 'AI usage' },
  admin:                { Icon: ShieldCheck, cls: 'text-muted-foreground', label: 'Admin event' },
};

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

const PAGE = 50;

export default function AuditPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState('all');

  const load = async (offset = skip) => {
    setLoading(true);
    try {
      const r = await adminApi.audit({ limit: PAGE, skip: offset });
      setRows(r.rows || []);
      setTotal(r.total || 0);
    } catch {
      toast.error('Could not load activity feed.');
    } finally { setLoading(false); }
  };

  // iter55w — `load` is stable via useCallback([]) so the empty-deps
  // array is intentional; not calling it out with a lint-disable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(0); }, []);

  const filtered = filter === 'all' ? rows : rows.filter((r) => r.source === filter);

  return (
    <section>
      <PanelHeader
        title="Activity & Audit Feed"
        subtitle="Combined timeline of admin events (role changes, presets, password rotations) and AI usage (chapter/chat generations, tokens, dollars)."
        onRefresh={() => load(skip)}
      />

      <div className="mt-3 flex items-center gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          data-testid="audit-filter-all"
          className="h-7 text-[11px]"
        >
          All ({rows.length})
        </Button>
        <Button
          variant={filter === 'admin_audit' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('admin_audit')}
          data-testid="audit-filter-admin"
          className="h-7 gap-1 text-[11px]"
        >
          <ShieldCheck className="h-3 w-3" /> Admin
        </Button>
        <Button
          variant={filter === 'usage_events' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('usage_events')}
          data-testid="audit-filter-usage"
          className="h-7 gap-1 text-[11px]"
        >
          <Coins className="h-3 w-3" /> AI usage
        </Button>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {total.toLocaleString()} events total · page {Math.floor(skip / PAGE) + 1}
        </span>
      </div>

      <div className="mt-3 overflow-hidden rounded-md border border-border bg-card" data-testid="audit-feed">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-[12px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> Loading activity…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-10 text-center text-[12px] text-muted-foreground">
            <Activity className="mx-auto h-5 w-5 text-muted-foreground/60" />
            <p className="mt-2">No events match this filter.</p>
          </div>
        )}
        <ul className="divide-y divide-border">
          {filtered.map((row, idx) => {
            const meta = KIND_META[row.kind] || KIND_META.admin;
            const Icon = meta.Icon;
            return (
              <li
                key={`${row.source}-${idx}-${row.at}`}
                data-testid={`audit-row-${idx}`}
                className="flex items-start gap-3 px-3 py-2 hover:bg-secondary/40"
              >
                <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-secondary', meta.cls)}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[12px] text-foreground">
                    <span className={cn('font-medium', meta.cls)}>{meta.label}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="truncate">{row.summary}</span>
                  </div>
                  <div className="font-mono-stat text-[10.5px] text-muted-foreground">
                    {row.source} · {relTime(row.at)} · {row.at}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-3 flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">
          Showing {filtered.length} of {total.toLocaleString()} events.
        </span>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm" variant="outline"
            disabled={loading || skip === 0}
            onClick={() => { const n = Math.max(0, skip - PAGE); setSkip(n); load(n); }}
            data-testid="audit-prev"
            className="h-7 text-[11px]"
          >
            ← Newer
          </Button>
          <Button
            size="sm" variant="outline"
            disabled={loading || rows.length < PAGE}
            onClick={() => { const n = skip + PAGE; setSkip(n); load(n); }}
            data-testid="audit-next"
            className="h-7 text-[11px]"
          >
            Older →
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => { setSkip(0); load(0); }}
            data-testid="audit-refresh"
            className="h-7 gap-1 text-[11px]"
          >
            <RefreshCcw className="h-3 w-3" /> Refresh
          </Button>
        </div>
      </div>
    </section>
  );
}
